import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, FileText, Bank, ArrowsLeftRight, Repeat, TrendUp, TrendDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/dashboard/stats');
      setStats(data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Clients',
      value: stats?.total_clients || 0,
      subtitle: `${stats?.onboarded_clients || 0} onboarded, ${stats?.pending_clients || 0} pending`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Invoices',
      value: stats?.total_invoices || 0,
      subtitle: `${stats?.matched_invoices || 0} matched, ${stats?.unmatched_invoices || 0} pending`,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Bank Transactions',
      value: stats?.total_transactions || 0,
      subtitle: `${stats?.matched_transactions || 0} matched, ${stats?.unmatched_transactions || 0} unmatched`,
      icon: Bank,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Recurring Invoices',
      value: stats?.recurring_invoices || 0,
      subtitle: 'Active recurring invoices',
      icon: Repeat,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="heading-2 mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to your accounting dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="border border-border card-hover" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline mb-2">{stat.title}</p>
                  <p className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{stat.subtitle}</p>
                </div>
                <div className={`p-3 rounded-md ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} weight="regular" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader className="pb-4">
            <CardTitle className="heading-4 flex items-center gap-2">
              <TrendDown className="w-5 h-5 text-red-500" />
              Total Purchases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-light tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(stats?.total_purchases)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">From all purchase invoices</p>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-4">
            <CardTitle className="heading-4 flex items-center gap-2">
              <TrendUp className="w-5 h-5 text-green-500" />
              Total Sales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-light tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(stats?.total_sales)}
            </p>
            <p className="text-sm text-muted-foreground mt-2">From all sales invoices</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border border-border">
        <CardHeader className="pb-4">
          <CardTitle className="heading-4">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/clients"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-add-client"
            >
              <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Add Client</p>
            </a>
            <a
              href="/invoices"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-upload-invoice"
            >
              <FileText className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Upload Invoice</p>
            </a>
            <a
              href="/bank-statements"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-upload-statement"
            >
              <Bank className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Bank Statement</p>
            </a>
            <a
              href="/matching"
              className="p-4 rounded-md border border-border hover:bg-muted transition-colors text-center"
              data-testid="quick-match"
            >
              <ArrowsLeftRight className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium">Match Transactions</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
